import { Button, Cluster, Container, Pill, Section, Stack, Tabs } from "@humaniti/ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ModalityTabs } from "../components/ModalityTabs";
import { MODALITIES, TASK_TYPE_COUNT } from "../content/taskTypes";
import { SEO } from "../lib/seo";

type Reason = {
  n: string;
  title: string;
  body: ReactNode;
  badge?: ReactNode;
};

const REASONS: Reason[] = [
  {
    n: "01",
    title: "Verified at the protocol layer",
    body: "Every label routes through N-Human consensus. Disagreement triggers adjudication. You don't audit quality — the network does.",
  },
  {
    n: "02",
    title: "Pay per verified label, not per hour",
    body: "Pricing is transparent and per-task. No setup fees, no minimums, no net-60 invoices.",
  },
  {
    n: "03",
    title: "Datasets you own",
    body: "Outputs are signed and addressable. Export anytime in 16+ formats. The dataset lives outside our database.",
  },
  {
    n: "04",
    title: "API + SDKs",
    body: (
      <>
        REST endpoints plus a Python and TypeScript SDK in early access — full reference docs at
        general availability. <a href="mailto:api@humaniti.io">Request early access →</a>
      </>
    ),
    badge: <Pill variant="accent">Early access</Pill>,
  },
];

// Task-type list lives in src/content/taskTypes.ts so PricingPage and the
// "Cover every modality" sections on ForBuildersPage + ForHumansPage stay in
// sync. Imported below as MODALITIES + TASK_TYPE_COUNT.

const QUALITY_CONTROLS: { n: string; title: string; body: string }[] = [
  {
    n: "01",
    title: "N-Human consensus",
    body: "Every label is independently produced by N Humans. Default N is 3. High-stakes work runs at 5 or 7.",
  },
  {
    n: "02",
    title: "Elo + Tier system",
    body: "Per-skill Elo + global composite. Verified accuracy raises tier. Tier gates which work a Human can pick up.",
  },
  {
    n: "03",
    title: "Gold questions",
    body: "Known-answer items injected into every batch. Continuous calibration without telling the Human which is which.",
  },
  {
    n: "04",
    title: "Dispute flow",
    body: "Disagreement triggers blind re-annotation. Steward adjudicates. Outcome posts to the audit log.",
  },
  {
    n: "05",
    title: "Stewards",
    body: "T3 Humans who adjudicate disputes, audit gold-question performance, and review edge cases.",
  },
  {
    n: "06",
    title: "Anti-gaming",
    body: "Behavioral telemetry, text-detection ensemble, device + wallet clustering. Slashing lands in Phase 3.",
  },
];

const INTEGRATION_TABS = [
  { value: "web", label: "Web wizard" },
  { value: "api", label: "API + SDK" },
  { value: "dag", label: "Visual pipeline builder" },
];

export const ForBuildersPage = () => (
  <>
    <SEO
      title="For Builders — Annotation, as protocol"
      description="Verified labels. Signed datasets. Direct payment to Humans. Pay per verified label, not per hour. No setup, no minimums, no vendor calls."
      path="/for-builders"
    />
    {/* 1. Hero */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <Pill variant="accent">For Builders</Pill>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-display)",
              lineHeight: "var(--lh-display)",
              letterSpacing: "-0.02em",
              maxWidth: 960,
            }}
          >
            Annotation, as protocol<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 620,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              color: "var(--fg-secondary)",
            }}
          >
            Verified labels. Signed datasets. Direct payment to Humans.
          </p>
          <Cluster gap="lg">
            {/* All Hire Humans CTAs route through /coming-soon until M1.6 launch
                — see ComingSoonPage.tsx for the rationale. */}
            <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
              <Button>Hire Humans →</Button>
            </Link>
          </Cluster>
        </Stack>
      </Container>
    </Section>

    {/* 2. Why Builders pick Humaniti — numbered editorial */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <div className="marketing-side-grid">
          <div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-label)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-secondary)",
              }}
            >
              § 02 — Why us
            </span>
            <h2
              style={{
                margin: "var(--space-4) 0 0",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: "var(--fs-h1)",
                lineHeight: "var(--lh-h1)",
                letterSpacing: "-0.01em",
              }}
            >
              Why Builders
              <span style={{ color: "var(--teal)" }}>/</span>
              <br />
              pick Humaniti
            </h2>
          </div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {REASONS.map((r) => (
              <li
                key={r.n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr",
                  gap: "var(--space-5)",
                  padding: "var(--space-6) 0",
                  borderTop: "1px solid var(--border-hairline)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-small)",
                    color: "var(--fg-secondary)",
                    paddingTop: "var(--space-1)",
                  }}
                >
                  {r.n}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontWeight: 500,
                      fontSize: "22px",
                      lineHeight: "32px",
                      marginBottom: "var(--space-2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-3)",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{r.title}.</span>
                    {r.badge}
                  </div>
                  <div
                    style={{
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                      maxWidth: 560,
                    }}
                  >
                    {r.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Container>
    </Section>

    {/* 3. The 17 task types */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 03 — Coverage
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Cover every modality<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            {TASK_TYPE_COUNT} task types across {MODALITIES.length} modalities. Tooling tuned per
            modality. Throughput scaled by the qualified pool.
          </p>
          {/* Tabbed coverage. Each tab is a modality; the panel grids the task
              types for that modality with name + 1-2 sentence description +
              output formats. The grid div has explicit width: 100% because
              its parent Stack defaults to align-items: flex-start (an
              intentional choice to keep inline children like Pill from
              stretching) — without that override, auto-fill would only fit
              one column. */}
          <ModalityTabs />
        </Stack>
      </Container>
    </Section>

    {/* 4. Quality controls */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 04 — Quality
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            How quality stays high<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {QUALITY_CONTROLS.map((q) => (
              <li
                key={q.n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "64px 1fr",
                  gap: "var(--space-5)",
                  padding: "var(--space-5) 0",
                  borderTop: "1px solid var(--border-hairline)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-small)",
                    color: "var(--fg-secondary)",
                    paddingTop: "var(--space-1)",
                  }}
                >
                  {q.n}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "28px",
                      marginBottom: "var(--space-2)",
                    }}
                  >
                    {q.title}.
                  </div>
                  <div
                    style={{
                      fontSize: "var(--fs-body)",
                      lineHeight: "var(--lh-body)",
                      color: "var(--fg-secondary)",
                      maxWidth: 640,
                    }}
                  >
                    {q.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <Link to="/how-it-works">Read the full methodology →</Link>
        </Stack>
      </Container>
    </Section>

    {/* 5. Integration paths */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 05 — Integration
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Three ways to start<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Tabs tabs={INTEGRATION_TABS} defaultValue="web">
            {(active) => {
              if (active === "web") {
                return (
                  <Stack gap="md" style={{ maxWidth: 720 }}>
                    <h3 style={{ margin: 0, fontSize: "var(--fs-h3)", fontWeight: 500 }}>
                      Web wizard.
                    </h3>
                    <p style={{ margin: 0, color: "var(--fg-secondary)" }}>
                      Drag-drop a sample. Pick a task type. Set N-Human consensus. Publish. No code,
                      no install. The shortest path from sample to verified labels.
                    </p>
                  </Stack>
                );
              }
              if (active === "api") {
                return (
                  <Stack gap="md" style={{ maxWidth: 720 }}>
                    <Cluster gap="md" align="center">
                      <h3 style={{ margin: 0, fontSize: "var(--fs-h3)", fontWeight: 500 }}>
                        API + SDK.
                      </h3>
                      <Pill variant="accent">Early access</Pill>
                    </Cluster>
                    <p style={{ margin: 0, color: "var(--fg-secondary)" }}>
                      REST endpoints, plus a Python and TypeScript SDK in early access. Wire
                      Humaniti into your training loop. Trigger active learning. Stream verified
                      labels to your warehouse.{" "}
                      <a href="mailto:api@humaniti.io">Request early access →</a>
                    </p>
                  </Stack>
                );
              }
              return (
                <Stack gap="md" style={{ maxWidth: 720 }}>
                  <h3 style={{ margin: 0, fontSize: "var(--fs-h3)", fontWeight: 500 }}>
                    Visual pipeline builder.
                  </h3>
                  <p style={{ margin: 0, color: "var(--fg-secondary)" }}>
                    Compose a DAG with Human, ML, and LLM nodes. Mix verified human labels with
                    model outputs and confidence-routing. Same canvas, same export, same audit
                    trail.
                  </p>
                </Stack>
              );
            }}
          </Tabs>
        </Stack>
      </Container>
    </Section>

    {/* 6. Pricing teaser */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § 06 — Pricing
          </span>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            What it costs<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            Per-label rate, multiplied by your chosen N-Human consensus, multiplied by volume, plus
            a transparent network fee. No setup, no minimums, no quotes. The full grid is on the
            pricing page.
          </p>
          <Link to="/pricing">See full pricing →</Link>
        </Stack>
      </Container>
    </Section>

    {/* 7. Closing CTA */}
    <Section style={{ background: "var(--ink)", color: "var(--paper)" }}>
      <Container size="full">
        <Stack gap="lg" alignItems="center" style={{ textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-h1)",
              lineHeight: "var(--lh-h1)",
              letterSpacing: "-0.01em",
            }}
          >
            Apply for a workspace<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p style={{ margin: 0, color: "var(--paper-2)", maxWidth: 560 }}>
            Approval typically inside 2 business days. We review every application.
          </p>
          <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
            <Button>Hire Humans →</Button>
          </Link>
        </Stack>
      </Container>
    </Section>
  </>
);
